import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { alpha, Box, ButtonBase, Tooltip } from '@mui/material';

export interface SegmentedOption<T extends string> {
  value: T;
  label: React.ReactNode;
  /** Shown on hover. */
  title?: string;
}

interface SegmentedToggleProps<T extends string> {
  value: T;
  options: SegmentedOption<T>[];
  onChange: (value: T) => void;
  size?: 'small' | 'medium';
  disabled?: boolean;
  /** Stretch to the container's width, options sharing it equally. */
  fullWidth?: boolean;
  'aria-label': string;
}

/**
 * The site's two- or three-way switch: a pill with the active option filled in the brand green. Used for the
 * probability / odds display and the model choice, in place of Material's default switch.
 */
export function SegmentedToggle<T extends string>({
  value,
  options,
  onChange,
  size = 'medium',
  disabled = false,
  fullWidth = false,
  'aria-label': ariaLabel,
}: SegmentedToggleProps<T>) {
  const height = size === 'small' ? 26 : 32;
  // A single green "thumb" sits behind the options and slides to the active one, instead of each option
  // switching its own background. Positions are measured from the rendered buttons.
  const containerRef = useRef<HTMLDivElement | null>(null);
  const buttonRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const [thumb, setThumb] = useState<{ left: number; width: number } | null>(null);
  const [animate, setAnimate] = useState(false); // no slide on first paint
  const measure = useCallback(() => {
    const btn = buttonRefs.current[value];
    const box = containerRef.current;
    if (!btn || !box) return;
    const b = btn.getBoundingClientRect();
    const c = box.getBoundingClientRect();
    setThumb({ left: b.left - c.left - box.clientLeft, width: b.width });
  }, [value]);
  useLayoutEffect(() => {
    measure();
  }, [measure, options.length, size, fullWidth]);
  useLayoutEffect(() => {
    if (thumb && !animate) {
      const id = requestAnimationFrame(() => setAnimate(true));
      return () => cancelAnimationFrame(id);
    }
  }, [thumb, animate]);
  useLayoutEffect(() => {
    // Re-measure when the pill resizes (fonts loading, full-width layouts, window resizes).
    const box = containerRef.current;
    if (!box || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => measure());
    ro.observe(box);
    return () => ro.disconnect();
  }, [measure]);
  // Two options behave like a switch: a click anywhere on the pill (either option or its edge) flips it.
  const isSwitch = options.length === 2;
  const other = () => options.find((o) => o.value !== value)?.value;
  const flip = () => {
    const next = other();
    if (!disabled && next !== undefined) onChange(next);
  };
  return (
    <Box
      ref={containerRef}
      onClick={isSwitch ? flip : undefined}
      role="radiogroup"
      aria-label={ariaLabel}
      sx={(theme) => ({
        display: fullWidth ? 'flex' : 'inline-flex',
        width: fullWidth ? '100%' : undefined,
        alignItems: 'center',
        gap: '2px',
        p: '3px',
        borderRadius: 999,
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.03)',
        opacity: disabled ? 0.5 : 1,
        flexShrink: 0,
        cursor: isSwitch && !disabled ? 'pointer' : undefined,
        position: 'relative',
      })}
    >
      <Box
        aria-hidden
        sx={(theme) => ({
          position: 'absolute',
          top: '3px',
          bottom: '3px',
          left: thumb?.left ?? 0,
          width: thumb?.width ?? 0,
          opacity: thumb ? 1 : 0,
          borderRadius: 999,
          bgcolor: theme.palette.primary.main,
          boxShadow: `0 0 12px ${alpha(theme.palette.primary.main, 0.35)}`,
          pointerEvents: 'none',
          transition: animate
            ? 'left 240ms cubic-bezier(0.4, 0, 0.2, 1), width 240ms cubic-bezier(0.4, 0, 0.2, 1)'
            : 'none',
          '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
        })}
      />
      {options.map((o) => {
        const active = o.value === value;
        const button = (
          <ButtonBase
            key={o.value}
            ref={(el: HTMLButtonElement | null) => {
              buttonRefs.current[o.value] = el;
            }}
            role="radio"
            aria-checked={active}
            disabled={disabled}
            onClick={(e) => {
              if (isSwitch) return; // the pill's own click handler flips it
              e.stopPropagation();
              if (!active) onChange(o.value);
            }}
            sx={(theme) => ({
              height,
              flex: fullWidth ? 1 : undefined,
              px: size === 'small' ? 1.25 : 1.75,
              borderRadius: 999,
              fontSize: size === 'small' ? '0.75rem' : '0.8125rem',
              fontWeight: 600,
              letterSpacing: '0.02em',
              whiteSpace: 'nowrap',
              position: 'relative',
              zIndex: 1,
              transition: theme.transitions.create(['background-color', 'color'], { duration: 200 }),
              color: active
                ? theme.palette.mode === 'dark'
                  ? '#0d1f12'
                  : theme.palette.primary.contrastText
                : theme.palette.text.secondary,
              // Until the thumb is measured, the active option paints its own background (no flash of no selection).
              bgcolor: active && !thumb ? theme.palette.primary.main : 'transparent',
              '&:hover': {
                color: active ? undefined : theme.palette.text.primary,
                bgcolor: active ? 'transparent' : alpha(theme.palette.primary.main, 0.1),
              },
              '&.Mui-focusVisible': { outline: `2px solid ${alpha(theme.palette.primary.main, 0.6)}`, outlineOffset: 1 },
            })}
          >
            {o.label}
          </ButtonBase>
        );
        return o.title ? (
          <Tooltip key={o.value} title={o.title}>
            <Box component="span" sx={{ display: 'flex', flex: fullWidth ? 1 : undefined }}>
              {button}
            </Box>
          </Tooltip>
        ) : (
          button
        );
      })}
    </Box>
  );
}
